import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getPurchaseOrders, updatePurchaseOrderStatus } from "../../apis/purchaseorder";
import { PurchaseOrder } from "../../interfaces/PurchaseOrder";
import {Tag, Button,Dropdown } from "antd";
import { format } from "date-fns";
import { message } from "antd";
import CommonPagination from "../../components/CommanPagination";

const getStatusColor = (status: string) => {
  switch (status.toLowerCase()) {
    case "cancelled":
      return "default";
    case "paid":
      return "green";
    case "pending":
      return "orange";
    case "draft":
      return "red";
    default:
      return "default";
  }
};

const getActionItems = (order: PurchaseOrder, navigate: any) => {
  const status = order.status?.toLowerCase();
  const items: any[] = [];

  // VIEW – only for paid & cancelled
  if (status === "paid" || status === "cancelled") {
    items.push({
      key: "view",
      label: (
        <span
          onClick={() => navigate(`/purchase-orders/view/${order.id}`)}
          className="cursor-pointer"
        >
          View
        </span>
      ),
    });
    return items;
  }

  // EDIT – for draft & pending
  if (status === "draft" || status === "pending") {
    items.push({
      key: "edit",
      label: (
        <span
          onClick={() => navigate(`/purchase-orders/edit/${order.id}`)}
          className="cursor-pointer"
        >
          Edit
        </span>
      ),
    });
  }

  // DRAFT actions
  if (status === "draft") {
    items.push(
      { key: "pending", label: "Pending" },
      { key: "paid", label: "Paid" },
      { key: "cancel", label: "Cancel" }
    );
  }

  // PENDING actions
  if (status === "pending") {
    items.push(
      { key: "paid", label: "Paid" },
      { key: "cancel", label: "Cancel" }
    );
  }

  return items;
};

const PurchaseOrdersList = () => {
  const navigate = useNavigate();
  const [meta, setMeta] = useState<any>();
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);

  useEffect(() => {
    fetchOrders({ page: 1, per_page: 10 });
  }, []);

  const fetchOrders = (params = {}) => {
    getPurchaseOrders({ per_page: 10, ...params })
      .then((res: any) => {
      setPurchaseOrders(res.data || []);
      setMeta(res.meta);
    });
  };

  const handleStatusChange = async (
    order: PurchaseOrder,
    status: 'draft' | 'pending' | 'paid' | 'cancelled'
  ) => {
    if (!order.id) return;

    try {
      await updatePurchaseOrderStatus(order.id, status);

      message.success(`Status updated to ${status}`);
      fetchOrders({ page: 1, per_page: 10 });
    } catch (err: any) {
      message.error(
        err?.response?.data?.message || "Failed to update status"
      );
    }
  };

// const onPaginationChange: PaginationProps["onChange"] = (page, pageSize) => {
//     fetchOrders({ page, per_page: pageSize });
//   };
const onPaginationChange = (page: number, pageSize?: number) => {
  fetchOrders({ page, per_page: pageSize || 10 }); 
};

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-base font-semibold leading-6 text-gray-900">
            Purchase Orders List
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            List of all purchase orders.
          </p>
        </div>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => navigate("/purchase-orders/add")}
            className="px-3 py-2 text-sm font-semibold text-white rounded-md shadow-sm bg-primary hover:bg-hover"
          >
            Add Purchase Order
          </button>
          <button
            type="button"
            className="px-3 py-2 text-sm font-semibold text-white rounded-md shadow-sm bg-primary hover:bg-hover"
          >
            Export
          </button>
        </div>
      </div>

      <div className="flow-root mt-8">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                      Order ID
                    </th>
                    <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
                      Supplier
                    </th>
                    <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
                      Date
                    </th>
                    <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
                      Total Amount
                    </th>
                    <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
                      Status
                    </th>
                    <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
                      Action
                    </th>
                  </tr>
                </thead>

                <tbody className="bg-white divide-y divide-gray-200">
                  {purchaseOrders.map((order, index) => (
                    <tr key={index}>
                      <td className="py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                        {order.poNumber}
                      </td>
                      <td className="py-4 px-3 text-sm text-gray-700">
                        {order.supplier?.name}
                      </td>
                      <td className="py-4 px-3 text-sm text-gray-700">
                        {order.orderDate
                          ? format(new Date(order.orderDate), "dd MMM yyyy, hh:mm a")
                          : "-"}
                      </td>
                      <td className="py-4 px-3 text-sm text-gray-700">
                        {order.totalAmount}
                      </td>
                      {/* <td
                        className={`py-4 px-3 text-sm font-medium ${order.status === "received"
                            ? "text-green-600"
                            : order.status === "pending"
                              ? "text-yellow-600"
                              : "text-red-600"
                          }`}
                      >
                        {order.status}
                      </td> */}
                      <td className="py-4 px-3 text-sm text-gray-700">
                        <div className="justify-center">
                          <Tag color={getStatusColor(order.status ?? "default")}>
                            {order.status?.replace("_", " ").toUpperCase() ?? "-"}
                          </Tag>
                        </div>
                      </td>
                      <td className="py-4 px-3 text-sm text-gray-700">
                        <Dropdown
                          menu={{
                            items: getActionItems(order, navigate),
                            onClick: ({ key }) => {
                              if (!order.id) return;

                              if (key === "pending") handleStatusChange(order, "pending");
                              if (key === "paid") handleStatusChange(order, "paid");
                              if (key === "cancel") handleStatusChange(order, "cancelled");
                            }
                          }}
                          placement="bottomLeft"
                        >
                          <Button>Actions</Button>
                        </Dropdown>
                      </td>
                    </tr>
                  ))}
                </tbody>

              </table>
            </div>
            {/* <div className="flex justify-end pt-5">
             <Pagination
              defaultCurrent={1}
              defaultPageSize={10}
              total={meta?.total}
              onChange={onPaginationChange}
             />
            </div> */}
            <CommonPagination meta={meta} onChange={onPaginationChange} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PurchaseOrdersList;
